import React, { CSSProperties, useCallback, useEffect, useMemo } from "react";
import { useParams, useHistory, useLocation, useRouteMatch, matchPath } from "react-router-dom";
import classNames from 'classnames';

import { useTranslate } from "../../../../../i18n/useTranslationModules";
import { BrandliveEvent, EBroadcastTypes, FeatureFlagsEnum, LanguagesAbbr, Session, SessionTypesEnum } from "../../../../../types/working-model";
import { EditorSizes, TemplateClassNames } from "../../../../../types/template-layouts";
import { formatStringEllipses, isMobile, isiOSMobile, getTemplateClassName } from "../../../../../utils/utils";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import ButtonPlain from "../../../../general-ui/button-plain/button-plain";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import { ParamsProps } from "../../../live-event";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { MarketingNavMenu } from "../../../marketing-page/navigation/marketing-nav";
import useTimestampStatus, { ETimestampStatus } from "../../../../../utils/use-timestamp-status";
import FiresideHeader from "../../fireside/fireside-header";
import { checkIsLive } from "../../../../../utils/live-checker";
import { useScreenMediaQuery } from "../../../../../utils/use-screen-media-query";
import { eventPath } from "../../../utils";
import { Tag } from '../../../../general-ui/tag/tag';
import { DebugDefaultRouteMap, DefaultRouteMap } from "../../../hooks/use-route-map";
import { PATHNAMES } from "../../../../../utils/admin-routing-utils";
import '../../../../../scss/live-event/base/session/session-header.scss';
import { OptionalComponent } from "../../../../../utils/optional-component";
import { LanguageDropdown } from "../../../../general-ui/language-dropdown/language-dropdown";
import useChangeLanguage from "../../../../../utils/use-change-language";

interface SessionHeaderProps {
	session: Session;
	eventBundle: BrandliveEvent;
	isEditor?: boolean;
	editorSize?: string;
	styleOverrides?: CSSProperties;
	previewLanguage?: LanguagesAbbr;
	hideProfile?: boolean;
	hideNavItems?: boolean;
}

const SessionHeader: React.FC<SessionHeaderProps> = ({
	session,
	eventBundle,
	isEditor,
	editorSize,
	styleOverrides,
	previewLanguage,
	hideProfile = false,
	hideNavItems = false
}) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const blProfile = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const chatPanelOpen = useTypedSelector(state => state.ChatReducer.isChatPanelOpen);

	const event = eventBundle || workingEvent;

	const { uuid, language, eventName }: ParamsProps = useParams();
	const history = useHistory();
	const location = useLocation();
	const routerMatch = useRouteMatch();
	const { t } = useTranslate(['session', 'homepage']);
	const { isLessThan1024, isLessThan640 } = useScreenMediaQuery();
	const timestampStatus = useTimestampStatus(session);
	const handleLanguageChange = useChangeLanguage(event);

	const template = getTemplateClassName(eventBundle.template.name);
	const isDesktop = editorSize ? editorSize === 'desktop' : undefined;
	const isTablet = editorSize ? editorSize === 'tablet' : undefined;
	const isMobileSize = isLessThan640 || isMobile || editorSize === EditorSizes.mobile;
	const isIphone = isiOSMobile?.();
	// orientation can either be 0, -90, 90, or 180 https://developer.apple.com/documentation/webkitjs/domwindow/1632568-orientation.
	const isLandscapeIphone = isIphone && (window.orientation === -90 || window.orientation === 90);

	const hasLandingPage = !!workingEvent?.homepage || !!eventBundle?.homepage;
	const workingEventSession = workingEvent?.sessions.find((s: Session) => s.session === session.session);
	const title = workingEventSession?.title;
	const displaySessionLanguageDropdown = session.languages.length > 1 && (event.settings.display_session_language_dropdown ?? false);

	let displayTitle: string;
	if (session.title[language]) {
		displayTitle = session.title[language].toString();
	} else if (title?.[language]) {
		displayTitle = title[language].toString();
	} else {
		displayTitle = session.title.base;
	}

	const isOnDemand = session.session_type === SessionTypesEnum.onDemand;
	const isBroadcast = session.session_type === SessionTypesEnum.broadcast;
	const isFireside = session.session_type === SessionTypesEnum.fireside;
	const isBreakout = session.session_type === SessionTypesEnum.breakoutRooms;
	const isIFrameBroadcast = session.session_type === SessionTypesEnum.broadcast && session.broadcast_type === EBroadcastTypes.embed;

	const attendeeInPersonModeEnabled = workingEvent?.settings.attendeeInPersonEnabled ?? eventBundle.settings.attendeeInPersonEnabled;
	const hideNavMenu = hideNavItems
		|| ((isFireside || isIFrameBroadcast)
			&& (isLessThan1024
				|| editorSize && editorSize === EditorSizes.tablet
				|| editorSize && editorSize === EditorSizes.mobile));

	// see comment in jsx for reasons for removing this
	// const tagText = () => {
	// 	if (isOnDemand) {
	// 		return t("homepage:On Demand");
	// 	} else if (isBroadcast) {
	// 		return t("homepage:Broadcast");
	// 	} else if (isBreakout) {
	// 		return t("homepage:Breakout Rooms");
	// 	} else if (isFireside) {
	// 		return t("homepage:Fireside");
	// 	} else {
	// 		return "";
	// 	}
	// };

	// const tagIcon = () => {
	// 	if (isOnDemand) {
	// 		return ICONS.ON_DEMAND;
	// 	} else if (isBroadcast) {
	// 		return ICONS.VIEWERS_EYE;
	// 	} else if (isBreakout) {
	// 		return ICONS.BREAKOUT_ROOMS;
	// 	} else if (isFireside) {
	// 		return ICONS.FIRESIDE;
	// 	} else {
	// 		return "";
	// 	}
	// };


	const tagIconColor = () => {
		switch (template) {
			case TemplateClassNames["Apollo"]:
				return COLORS.WHITE;
			case TemplateClassNames["Studio 54"]:
			case TemplateClassNames["O2"]:
				return COLORS.BLACK;
			default:
				return COLORS.WHITE;
		}
	};

	// for firesides and breakouts we need to store the original location from which the session was accessed
	// in order to properly route the user when they click the back arrow (or they could be taken back on stage or into their room)
	useEffect(() => {
		if (location.state) {
			sessionStorage.setItem('backTo', (location.state as any).from);
		}
	}, [location.state]);

	const backButton = (): void => {
		if (isFireside || isBreakout) {
			const path = sessionStorage.getItem('backTo') ?? `/${eventPath(eventName, uuid)}/${language}/sessions`;
			history.push(path);
		} else {
			history.goBack();
		}
	};

	{/*
		Do not show back button if breakout and we're on the sessions or home page.
		This follows the same pattern for other session types since they also don't show the back button on Home/Agenda pages
	*/ }
	const hideBackButtonForBreakout = useCallback(() => {
		return isBreakout && [
			DebugDefaultRouteMap.Home,
			DefaultRouteMap.Home,
			DebugDefaultRouteMap.Sessions,
			DefaultRouteMap.Sessions,
			PATHNAMES.Event.SessionsPath,
			PATHNAMES.Event.HomePath,
		].includes(routerMatch.path);
	}, [isBreakout, routerMatch.path]);

	const isSingleSession = (event)?.sessions?.length === 1;
	// if single session on demand and screen size is in between mobile and desktop, then make header static
	const isSingleSessionTablet = useMemo(() => {
		const match = matchPath(history.location.pathname, {
			path: [
				DebugDefaultRouteMap.Landing,
				DefaultRouteMap.Landing,
				DefaultRouteMap.Session,
				DebugDefaultRouteMap.Session,
			],
			exact: true
		});
		const liveEventStaticHeader = match
			&& isSingleSession
			&& !isMobile
			&& !isLessThan640
			&& isLessThan1024;

		const editorStaticHeader = isSingleSession
			&& isEditor
			&& isTablet;

		const onDemandOrBroadcast = isOnDemand || isBroadcast;

		return (liveEventStaticHeader || editorStaticHeader) && onDemandOrBroadcast;

	}, [
		history.location.pathname,
		isSingleSession,
		isLessThan640,
		isLessThan1024,
		isEditor,
		isTablet,
		isOnDemand,
		isBroadcast,
	]);

	return (
		<div
			className={classNames("session-header", template, {
				'breakout-session': isBreakout,
				'fireside-session': isFireside,
				'has-navigation': hasLandingPage,
				[timestampStatus]: timestampStatus,
				'is-mobile': isMobileSize,
				'is-desktop': !isLessThan1024,
				'is-landscape-iphone': isLandscapeIphone,
				'chat-open': chatPanelOpen,
				'static-header': isSingleSessionTablet,
			})}
			style={styleOverrides}
		>
			<header
				className={classNames(template, {
					'is-mobile': isMobileSize,
					'display-language-dropdown': isMobileSize && displaySessionLanguageDropdown
				})}
				style={{ pointerEvents: (isEditor || (previewMode === 'session')) ? 'none' : 'auto' }}>
				<div className={classNames("session-titlebox", { "dark-mode": session?.session_chat_dark_mode_enabled })}>
					{!hasLandingPage || hideBackButtonForBreakout() ? null : (
						<ButtonPlain className={"back-button"} onclick={backButton} icon={ICONS.ARROW_LEFT} color={"inherit"} />
					)}
					{(isFireside && checkIsLive(session) && !featureFlags[FeatureFlagsEnum.firesides_broadcasting]) ? (
						<FiresideHeader
							videoCallConnected={false} // videoCallConnected, videoProvider, cameraActive are only used when isOnStage which will never be the case here
							// videoProvider={false}
							cameraActive={false}
							templateClassName={template}
							blProfile={blProfile}
							session={session}
							isOnStage={false}
						/>
					) : (
						<div className="session-title-text">
							<div className="attendee-in-person-enabled-tag">
								{timestampStatus !== ETimestampStatus.preLive && (
									<TypographyItem tagName={"h5"}>{formatStringEllipses(displayTitle, 100)}</TypographyItem>
								)
									// removing because it looks crazy and doesn't make sense to me. Can put back in later if we come up with a good reason.
									// <div className={classNames("title-and-tag", {"dark-mode": session?.session_chat_dark_mode_enabled})}>
									// 	<TypographyItem tagName={"h5"}>{formatStringEllipses(displayTitle, 100)}</TypographyItem>
									// 	<Tag
									// 		classNameTag={classNames('')}
									// 		text={tagText()}
									// 		icon={tagIcon()}
									// 		iconColor={tagIconColor()}
									// 	/>
									// </div>
								}
								{timestampStatus === ETimestampStatus.preLive &&
									<TypographyItem tagName={"h5"}>{t("session:Back")}</TypographyItem>}
								{attendeeInPersonModeEnabled &&
									<span>
										{isLessThan1024 ? (
											<div style={{ display: 'flex', flexDirection: 'row' }}>
												<Icon name={ICONS.PEOPLE} size={24} color={tagIconColor()} />
											</div>
										) : (
											<Tag
												classNameTag={classNames('tag-container-type')}
												text={t('session:In-Person View') as string}
												icon={ICONS.PEOPLE}
												iconColor={tagIconColor()}
												iconSize={24}
											/>
										)}
									</span>
								}
							</div>
						</div>
					)}
				</div>
				<OptionalComponent display={isMobileSize && displaySessionLanguageDropdown}>
					<div className="language-dropdown-container">
						<LanguageDropdown languages={session.languages as LanguagesAbbr[]} currentLanguageCode={language} useISOLangCodes onLanguageChange={handleLanguageChange} isNoScroll={false} animateDropdown />
					</div>
				</OptionalComponent>
				{hideNavMenu ? null : (
					<MarketingNavMenu hideProfile={hideProfile} isEditorDesktop={isDesktop} previewLanguage={previewLanguage} template={template} />
				)}
			</header>
		</div>
	);
};

export default SessionHeader;
